import React, { useMemo, useState } from 'react';
import { ClipboardCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { errMsg } from './recoveryUi';

export default function CheckinForm({ planId, plan, doses, reached, onDone }) {
  const meds = plan.medicines.filter((m) => !m.sos);
  const prefillMissed = useMemo(() => {
    const now = Date.now();
    return meds.filter((m) => doses.some((d) => d.med_key === m.key && new Date(d.due_at).getTime() <= now && d.status !== 'taken')).map((m) => m.key);
  }, [meds, doses]);
  const [missed, setMissed] = useState(prefillMissed);
  const [ms, setMs] = useState([]);
  const [flags, setFlags] = useState({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const toggle = (arr, set, k) => set(arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]);
  const open = plan.milestones.filter((m) => !reached[m.id]);
  const allAnswered = plan.red_flags.every((f) => typeof flags[f.id] === 'boolean');

  const submit = async () => {
    if (!allAnswered) { toast.error('Please answer every question with Yes or No'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/patient/recovery/${planId}/checkin`, {
        meds_missed: missed, milestones_reached: ms, red_flags: flags, note });
      toast.success(data.red_flags_triggered.length ? 'Check-in saved — please follow the self-care steps below' : 'Check-in saved. Thank you!');
      onDone();
    } catch (e) { toast.error(errMsg(e, 'Could not save check-in')); } finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl bg-white border-2 border-teal-600 p-4 shadow-sm" data-testid="recovery-checkin-form">
      <h2 className="text-[15px] font-extrabold text-slate-900 flex items-center gap-2"><ClipboardCheck size={17} className="text-teal-700" /> Today's check-in</h2>
      {meds.length > 0 && (
        <div className="mt-3">
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Took all medicines? Tick any you missed</div>
          {meds.map((m) => (
            <label key={m.key} className="mt-2 flex items-center gap-3 text-[14px]">
              <input type="checkbox" className="w-5 h-5 accent-red-600" checked={missed.includes(m.key)} onChange={() => toggle(missed, setMissed, m.key)} data-testid={`recovery-checkin-missed-${m.key}`} />
              {m.name}
            </label>
          ))}
        </div>
      )}
      {open.length > 0 && (
        <div className="mt-4">
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Milestones reached</div>
          {open.map((m) => (
            <label key={m.id} className="mt-2 flex items-center gap-3 text-[14px]">
              <input type="checkbox" className="w-5 h-5 accent-teal-600" checked={ms.includes(m.id)} onChange={() => toggle(ms, setMs, m.id)} data-testid={`recovery-checkin-milestone-${m.id}`} />
              {m.title} <span className="text-[11px] text-slate-400">Day {m.target_day}</span>
            </label>
          ))}
        </div>
      )}
      <div className="mt-4 space-y-3">
        <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Do you have any of these?</div>
        {plan.red_flags.map((f) => (
          <div key={f.id} className="flex items-center gap-2" data-testid={`recovery-checkin-flag-${f.id}`}>
            <div className="flex-1 text-[14px] text-slate-800">{f.question}</div>
            {[['Yes', true], ['No', false]].map(([lbl, v]) => (
              <button key={lbl} type="button" onClick={() => setFlags({ ...flags, [f.id]: v })}
                      className="px-3.5 py-2 rounded-lg text-[13px] font-bold border transition-colors"
                      style={flags[f.id] === v ? { background: v ? '#b91c1c' : '#0f766e', color: '#fff', borderColor: 'transparent' } : { borderColor: '#cbd5e1' }}
                      data-testid={`recovery-checkin-flag-${f.id}-${lbl.toLowerCase()}`}>{lbl}</button>
            ))}
          </div>
        ))}
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={600} rows={2} placeholder="Anything else for your surgeon? (optional)"
                className="mt-4 w-full rounded-xl border border-slate-300 p-3 text-[14px] outline-none focus:border-teal-600" data-testid="recovery-checkin-note" />
      <button type="button" onClick={submit} disabled={busy}
              className="mt-3 w-full py-3.5 rounded-xl bg-teal-700 text-white font-extrabold text-[15px] flex items-center justify-center gap-2 disabled:opacity-60" data-testid="recovery-checkin-submit">
        {busy && <Loader2 size={16} className="animate-spin" />} Submit check-in
      </button>
    </section>
  );
}
